import { Group, Modal, Stack } from '@mantine/core';
import { Notifications, notifications } from '@mantine/notifications';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skyDark } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';
import { DEFAULT_POSITION } from './notifications.constants';
import { createNotification, notificationIcons, setModalHandler } from './notifications.helpers';
import type { NotificationConfig, NotificationContextType } from './notifications.type';
import { ReactNode } from 'react';

const NotificationContext = createContext<NotificationContextType>({} as NotificationContextType);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const i18n = useTranslation();
  const [modalOpened, setModalOpened] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title?: string | ReactNode;
    message: string;
    type?: NotificationConfig['type'];
  }>({ message: '' });

  // biome-ignore lint/correctness/useExhaustiveDependencies: necessary to set the modal handler only once
  useEffect(() => {
    setModalHandler(showFullTextModal);
  }, []);

  const showNotification = (config: NotificationConfig): string => {
    return createNotification(config, showFullTextModal);
  };

  const hideNotification = (id: string) => {
    notifications.hide(id);
  };

  const showFullTextModal = (
    title: string | ReactNode | undefined,
    message: string,
    type?: NotificationConfig['type'],
  ) => {
    setModalContent({ title, message, type });
    setModalOpened(true);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification, showFullTextModal }}>
      <Notifications position={DEFAULT_POSITION} />

      {children}

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        withCloseButton={false}
        centered
        size={532}
        padding={24}
        styles={{
          content: {
            borderRadius: 8,
            boxShadow: 'var(--box-shadow-big-soft)',
          },
        }}
      >
        <Stack gap={24}>
          <Group justify="space-between" align="center">
            <Group align="center" gap={8}>
              {modalContent.type && notificationIcons[modalContent.type]}
              <Text size="large" weight="bold">
                {modalContent.title || i18n.t('common.notification')}
              </Text>
            </Group>
            <Icon
              name="close"
              variant="outlined"
              size={24}
              color={skyDark}
              onClick={() => setModalOpened(false)}
              pointer
            />
          </Group>
          <Text>{modalContent.message}</Text>
          <Divider />
          <Group justify="center">
            <Button radius="xl" size="sm" variant="outline" onClick={() => setModalOpened(false)}>
              {i18n.t('common.actions.close')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </NotificationContext.Provider>
  );
}

const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }

  return context;
};

export { NotificationContext, useNotification };
